import React, { useState, useEffect } from "react"
import { Col, Row, Spinner, Table } from "reactstrap"
import { Link, useLocation } from "react-router-dom"
import api from "API/Api"
import { usePermissions } from "API/PermissionContext"

const PaymentProcess = () => {
  const { canApprove, canEdit, MODULE } = usePermissions()
  const MID = MODULE.PAYMENTS

  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const viewBatchId = params.get("batch")

  const [step, setStep] = useState(viewBatchId ? 3 : 1)
  const [loading, setLoading] = useState(false)

  // Step 1: Period
  const [periodFrom, setPeriodFrom] = useState("")
  const [periodTo, setPeriodTo] = useState("")
  const [suggestedPeriod, setSuggestedPeriod] = useState(null)

  // Step 2: Scope
  const [scopeType, setScopeType] = useState("all")
  const [scopeId, setScopeId] = useState("")
  const [routes, setRoutes] = useState([])

  // Step 3: Preview
  const [batch, setBatch] = useState(null)
  const [entries, setEntries] = useState([])
  const [statusCounts, setStatusCounts] = useState({})
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState("")

  // Filters
  const [filterType, setFilterType] = useState("")
  const [filterStatus, setFilterStatus] = useState("")
  const [filterSearch, setFilterSearch] = useState("")

  useEffect(() => {
    loadInitial()
    if (viewBatchId) loadBatch(viewBatchId)
  }, [])

  const loadInitial = async () => {
    try {
      const [periodResp, routesResp] = await Promise.all([
        api.get("payment-config/suggested-period"),
        api.get("routes?per_page=500")
      ])
      if (periodResp.data.success) {
        setSuggestedPeriod(periodResp.data.data)
        setPeriodFrom(periodResp.data.data.period?.from || "")
        setPeriodTo(periodResp.data.data.period?.to || "")
      }
      const rd = routesResp.data?.data
      setRoutes(Array.isArray(rd) ? rd : rd?.data || [])
    } catch (e) { console.error(e) }
  }

  const loadBatch = async (batchId) => {
    setLoading(true)
    try {
      const resp = await api.get("payment-batch/" + batchId)
      if (resp.data.success) {
        setBatch(resp.data.data.batch)
        setEntries(resp.data.data.entries || [])
        setStatusCounts(resp.data.data.status_counts || {})
      }
    } catch (e) { console.error(e) }
    setLoading(false)
  }

  const generateBatch = async () => {
    if (!periodFrom || !periodTo) { setError("Select billing period"); return }
    if (periodFrom > periodTo) { setError("From date must be before To date"); return }
    setGenerating(true); setError("")
    try {
      const resp = await api.post("payment-batch/generate", {
        period_from: periodFrom, period_to: periodTo,
        scope_type: scopeType, scope_id: scopeId || null
      })
      if (resp.data.success) {
        await loadBatch(resp.data.batch_id)
        setStep(3)
      } else {
        setError(resp.data.error || "Failed to generate batch")
        if (resp.data.batch_id) { await loadBatch(resp.data.batch_id); setStep(3) }
      }
    } catch (e) {
      setError(e.response?.data?.error || "Generation failed")
    }
    setGenerating(false)
  }

  const approveBatch = async () => {
    if (!batch) return
    setLoading(true)
    try {
      await api.post(`payment-batch/${batch.id}/approve`)
      await loadBatch(batch.id)
    } catch (e) { setError(e.response?.data?.error || "Approval failed") }
    setLoading(false)
  }

  const processBatch = async () => {
    if (!batch) return
    if (!window.confirm("Process all approved payments? Cash will be marked paid, bank CSV will be generated.")) return
    setLoading(true)
    try {
      const resp = await api.post(`payment-batch/${batch.id}/process`)
      if (resp.data.success) { await loadBatch(batch.id) }
      else setError(resp.data.error || "Processing failed")
    } catch (e) { setError(e.response?.data?.error || "Processing failed") }
    setLoading(false)
  }

  const holdEntry = async (id) => {
    try {
      await api.post(`payment-ledger/${id}/hold`, { reason: "Held by admin" })
      await loadBatch(batch.id)
    } catch(e) { console.error(e) }
  }

  const releaseEntry = async (id) => {
    try {
      await api.post(`payment-ledger/${id}/release`)
      await loadBatch(batch.id)
    } catch(e) { console.error(e) }
  }

  const skipEntry = async (id) => {
    try {
      await api.post(`payment-ledger/${id}/skip`, { reason: "Skipped by admin" })
      await loadBatch(batch.id)
    } catch(e) { console.error(e) }
  }

  const markPaid = async (id) => {
    try {
      await api.post(`payment-ledger/${id}/mark-paid`, { payment_ref: "CASH-MANUAL" })
      await loadBatch(batch.id)
    } catch(e) { console.error(e) }
  }

  const downloadBankFile = () => {
    if (!batch) return
    window.open(`${api.defaults.baseURL}/payment-batch/${batch.id}/bank-file`, "_blank")
  }

  const exportLedger = () => {
    if (!batch) return
    window.open(`${api.defaults.baseURL}/payment-batch/${batch.id}/export`, "_blank")
  }

  const filteredEntries = entries.filter(e => {
    if (filterType && e.payee_type !== filterType) return false
    if (filterStatus && e.payment_status !== filterStatus) return false
    if (filterSearch && !(e.payee_name||"").toLowerCase().includes(filterSearch.toLowerCase())) return false
    return true
  })

  const statusBadge = (s) => {
    const m = { pending:"bg-secondary", approved:"bg-info", processing:"bg-primary", paid:"bg-success", failed:"bg-danger", held:"bg-warning text-dark", skipped:"bg-light text-dark" }
    return <span className={`badge ${m[s]||"bg-secondary"}`} style={{fontSize:"10px"}}>{(s||"").toUpperCase()}</span>
  }

  const modeBadge = (mode) => {
    const m = { cash:"💵 Cash", bank:"🏦 Bank", upi:"📱 UPI" }
    return <span style={{fontSize:"11px",fontWeight:"500"}}>{m[mode]||mode}</span>
  }

  const stepStyle = (n) => ({
    flex:1, padding:"10px 16px", textAlign:"center",
    background: step >= n ? "#6366f1" : "#f1f5f9",
    color: step >= n ? "#fff" : "#94a3b8",
    fontWeight: step === n ? "700" : "500",
    borderRadius: n===1 ? "8px 0 0 8px" : n===3 ? "0 8px 8px 0" : "0",
    fontSize:"13px", cursor:"pointer",
    transition: "all 0.3s",
  })

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}><h4><i className="fas fa-play-circle me-2" style={{color:"#6366f1"}}></i>Process Payment</h4></Col>
          <Col md={6} style={{textAlign:"right"}}>
            <Link className="dashBtn" to="/payment-dashboard"><i className="fas fa-chevron-left"></i> <b>Dashboard</b></Link>
          </Col>
        </Row>
      </div>
      <div className="dashContent">

        {/* ── Step Indicator ── */}
        <div style={{display:"flex",marginBottom:"24px"}}>
          <div style={stepStyle(1)} onClick={()=>!batch && setStep(1)}>1. Select Period</div>
          <div style={stepStyle(2)} onClick={()=>!batch && setStep(2)}>2. Scope & Generate</div>
          <div style={stepStyle(3)}>3. Review & Process</div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show" style={{fontSize:"13px"}}>
            <i className="fas fa-exclamation-circle me-2"></i>{error}
            <button type="button" className="btn-close" onClick={()=>setError("")}></button>
          </div>
        )}

        {/* ══════ STEP 1: Period ══════ */}
        {step === 1 && (
          <div style={{background:"#fff",borderRadius:"12px",padding:"24px",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <h6 style={{color:"#334155",fontWeight:"600",marginBottom:"16px"}}><i className="fas fa-calendar-alt me-2"></i>Select Billing Period</h6>

            {suggestedPeriod && (
              <div style={{background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:"8px",padding:"12px 16px",marginBottom:"16px"}}>
                <div style={{fontSize:"12px",fontWeight:"600",color:"#166534"}}><i className="fas fa-magic me-1"></i>Suggested Period (based on your config)</div>
                <div style={{fontSize:"14px",color:"#1e293b",marginTop:"4px"}}>
                  <strong>{suggestedPeriod.period?.from}</strong> to <strong>{suggestedPeriod.period?.to}</strong>
                  <span className="badge bg-info ms-2">{suggestedPeriod.cycle}</span>
                </div>
                <button className="btn btn-sm btn-success mt-2" onClick={()=>{setPeriodFrom(suggestedPeriod.period?.from||"");setPeriodTo(suggestedPeriod.period?.to||"");setStep(2)}}>
                  <i className="fas fa-check me-1"></i>Use Suggested Period
                </button>
              </div>
            )}

            <Row>
              <Col md={5}><div className="mb-3"><label className="form-label fw-bold">Period From</label><input type="date" className="form-control" value={periodFrom} onChange={e=>setPeriodFrom(e.target.value)} /></div></Col>
              <Col md={5}><div className="mb-3"><label className="form-label fw-bold">Period To</label><input type="date" className="form-control" value={periodTo} onChange={e=>setPeriodTo(e.target.value)} /></div></Col>
              <Col md={2} className="d-flex align-items-end mb-3">
                <button className="btn btn-primary w-100" onClick={()=>setStep(2)} disabled={!periodFrom||!periodTo}>Next →</button>
              </Col>
            </Row>
          </div>
        )}

        {/* ══════ STEP 2: Scope & Generate ══════ */}
        {step === 2 && (
          <div style={{background:"#fff",borderRadius:"12px",padding:"24px",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <h6 style={{color:"#334155",fontWeight:"600",marginBottom:"16px"}}><i className="fas fa-bullseye me-2"></i>Select Scope & Generate</h6>

            <div style={{background:"#f8fafc",borderRadius:"8px",padding:"12px 16px",marginBottom:"16px"}}>
              <strong>Period:</strong> {periodFrom} → {periodTo}
              <button className="btn btn-link btn-sm" onClick={()=>setStep(1)}>Change</button>
            </div>

            <Row className="mb-3">
              <Col md={4}>
                <label className="form-label fw-bold">Payment Scope</label>
                <select className="form-control" value={scopeType} onChange={e=>{setScopeType(e.target.value);setScopeId("")}}>
                  <option value="all">All Routes (Full Company)</option>
                  <option value="route">Specific Route</option>
                </select>
              </Col>
              {scopeType === "route" && (
                <Col md={4}>
                  <label className="form-label fw-bold">Select Route</label>
                  <select className="form-control" value={scopeId} onChange={e=>setScopeId(e.target.value)}>
                    <option value="">Select Route</option>
                    {routes.map((r,i)=><option key={i} value={r.id}>{r.routeName}</option>)}
                  </select>
                </Col>
              )}
            </Row>

            <div style={{display:"flex",gap:"12px"}}>
              <button className="btn btn-outline-secondary" onClick={()=>setStep(1)}>← Back</button>
              <button className="btn btn-primary" onClick={generateBatch} disabled={generating || (scopeType==="route"&&!scopeId)}>
                {generating ? <><Spinner size="sm" className="me-1"/>Generating...</> : <><i className="fas fa-calculator me-1"></i>Generate Payment Batch</>}
              </button>
            </div>
          </div>
        )}

        {/* ══════ STEP 3: Review & Process ══════ */}
        {step === 3 && batch && (
          <>
            {/* Batch Summary */}
            <div style={{background:"linear-gradient(135deg, #6366f110, #818cf810)",border:"1px solid #c7d2fe",borderRadius:"12px",padding:"20px",marginBottom:"20px"}}>
              <Row>
                <Col md={3}>
                  <div style={{fontSize:"11px",color:"#6c757d",textTransform:"uppercase"}}>Batch Code</div>
                  <div style={{fontSize:"16px",fontWeight:"700",color:"#6366f1"}}>{batch.batch_code}</div>
                </Col>
                <Col md={3}>
                  <div style={{fontSize:"11px",color:"#6c757d",textTransform:"uppercase"}}>Period</div>
                  <div style={{fontSize:"13px",fontWeight:"600"}}>{batch.period_from} → {batch.period_to}</div>
                </Col>
                <Col md={2}>
                  <div style={{fontSize:"11px",color:"#6c757d",textTransform:"uppercase"}}>Total Payees</div>
                  <div style={{fontSize:"20px",fontWeight:"700"}}>{batch.total_payees}</div>
                </Col>
                <Col md={2}>
                  <div style={{fontSize:"11px",color:"#6c757d",textTransform:"uppercase"}}>Net Payable</div>
                  <div style={{fontSize:"20px",fontWeight:"700",color:"#059669"}}>₹{Number(batch.total_net||0).toLocaleString("en-IN")}</div>
                </Col>
                <Col md={2}>
                  <div style={{fontSize:"11px",color:"#6c757d",textTransform:"uppercase"}}>Status</div>
                  <div style={{marginTop:"4px"}}>{statusBadge(batch.status)}</div>
                </Col>
              </Row>

              <div style={{display:"flex",gap:"16px",marginTop:"12px",flexWrap:"wrap"}}>
                {batch.cash_count > 0 && <span style={{fontSize:"12px",color:"#6c757d"}}>💵 Cash: {batch.cash_count} (₹{Number(batch.cash_total||0).toLocaleString("en-IN")})</span>}
                {batch.bank_count > 0 && <span style={{fontSize:"12px",color:"#6c757d"}}>🏦 Bank: {batch.bank_count} (₹{Number(batch.bank_total||0).toLocaleString("en-IN")})</span>}
                {batch.upi_count > 0 && <span style={{fontSize:"12px",color:"#6c757d"}}>📱 UPI: {batch.upi_count} (₹{Number(batch.upi_total||0).toLocaleString("en-IN")})</span>}
                <span style={{fontSize:"12px",color:"#dc2626"}}>Deductions: ₹{Number(batch.total_deductions||0).toLocaleString("en-IN")}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{display:"flex",gap:"8px",marginBottom:"16px",flexWrap:"wrap"}}>
              {batch.status === "draft" && <button className="btn btn-info btn-sm" onClick={approveBatch} disabled={loading}><i className="fas fa-check me-1"></i>Approve Batch</button>}
              {batch.status === "approved" && <button className="btn btn-success" onClick={processBatch} disabled={loading}><i className="fas fa-play-circle me-1"></i>Process Payments</button>}
              {batch.bank_file_generated && <button className="btn btn-outline-success btn-sm" onClick={downloadBankFile}><i className="fas fa-download me-1"></i>Download Bank CSV</button>}
              <button className="btn btn-outline-primary btn-sm" onClick={exportLedger}><i className="fas fa-file-csv me-1"></i>Export Full Ledger</button>
              <button className="btn btn-outline-secondary btn-sm" onClick={()=>loadBatch(batch.id)}><i className="fas fa-sync me-1"></i>Refresh</button>
            </div>

            {/* Filters */}
            <Row className="mb-3">
              <Col md={3}><input type="text" className="form-control form-control-sm" placeholder="Search name..." value={filterSearch} onChange={e=>setFilterSearch(e.target.value)} /></Col>
              <Col md={2}>
                <select className="form-control form-control-sm" value={filterType} onChange={e=>setFilterType(e.target.value)}>
                  <option value="">All Types</option>
                  <option value="producer">Producers</option>
                  <option value="vendor">Vendors</option>
                </select>
              </Col>
              <Col md={2}>
                <select className="form-control form-control-sm" value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="processing">Processing</option>
                  <option value="paid">Paid</option>
                  <option value="held">Held</option>
                  <option value="skipped">Skipped</option>
                  <option value="failed">Failed</option>
                </select>
              </Col>
            </Row>

            {/* Entries Table */}
            <div className="table-responsive" style={{background:"#fff",borderRadius:"8px"}}>
              <Table className="table mb-0" style={{fontSize:"12px"}}>
                <thead>
                  <tr style={{background:"#f8fafc",color:"#64748b",textTransform:"uppercase",letterSpacing:"0.5px",fontSize:"10px"}}>
                    <th>#</th><th>Type</th><th>Name</th><th>Mobile</th><th>Qty (Ltrs)</th>
                    <th>Gross (₹)</th><th>Incentive</th><th>Deductions (₹)</th><th>Net (₹)</th>
                    <th>Mode</th><th>Status</th><th>Reason</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? <tr><td colSpan={13} style={{textAlign:"center",padding:"20px"}}><Spinner size="sm" /></td></tr> :
                  filteredEntries.length === 0 ? <tr><td colSpan={13} style={{textAlign:"center",padding:"20px",color:"#94a3b8"}}>No payment entries</td></tr> :
                  filteredEntries.map((e, i) => (
                    <tr key={e.id||i} style={{borderBottom:"1px solid #f1f5f9"}}>
                      <td>{i+1}</td>
                      <td><span className={`badge ${e.payee_type==="producer"?"bg-primary":"bg-info"}`} style={{fontSize:"9px"}}>{e.payee_type?.toUpperCase()}</span></td>
                      <td style={{fontWeight:"600"}}>{e.payee_name}</td>
                      <td>{e.payee_mobile||"—"}</td>
                      <td>{e.total_qty}</td>
                      <td style={{color:"#0284c7"}}>₹{Number(e.gross_amount||0).toLocaleString("en-IN")}</td>
                      <td style={{color:"#059669"}}>₹{Number(e.incentive_amount||0).toLocaleString("en-IN")}</td>
                      <td style={{color:"#dc2626"}}>₹{Number(e.total_deductions||0).toLocaleString("en-IN")}</td>
                      <td style={{fontWeight:"700",color:"#059669"}}>₹{Number(e.net_payable||0).toLocaleString("en-IN")}</td>
                      <td>{modeBadge(e.payment_mode)}</td>
                      <td>{statusBadge(e.payment_status)}</td>
                      <td style={{fontSize:"10px",color:"#ef4444",maxWidth:"120px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.held_reason||"—"}</td>
                      <td style={{whiteSpace:"nowrap"}}>
                        {["pending","approved"].includes(e.payment_status) && <button className="btn btn-outline-warning btn-sm me-1" style={{fontSize:"9px",padding:"1px 5px"}} onClick={()=>holdEntry(e.id)} title="Hold">⏸</button>}
                        {e.payment_status==="held" && <button className="btn btn-outline-success btn-sm me-1" style={{fontSize:"9px",padding:"1px 5px"}} onClick={()=>releaseEntry(e.id)} title="Release">▶</button>}
                        {["pending","approved","held"].includes(e.payment_status) && <button className="btn btn-outline-danger btn-sm me-1" style={{fontSize:"9px",padding:"1px 5px"}} onClick={()=>skipEntry(e.id)} title="Skip">✕</button>}
                        {e.payment_status==="approved" && e.payment_mode==="cash" && <button className="btn btn-outline-success btn-sm" style={{fontSize:"9px",padding:"1px 5px"}} onClick={()=>markPaid(e.id)} title="Mark Paid">✓</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <div style={{fontSize:"11px",color:"#94a3b8",marginTop:"8px"}}>Showing {filteredEntries.length} of {entries.length} entries</div>
          </>
        )}
      </div>
    </div>
  )
}

export default PaymentProcess
